import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText } from "lucide-react";
import api from "@/lib/api";
import { BlurImage } from "@/components/site/BlurImage";
import { useContent } from "@/context/ContentContext";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const ArticleCard = ({ article, index }) => (
  <motion.div
    data-testid={`article-card-${index}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
  >
    <Link
      to={`/articles/${article.id}`}
      className="group block bg-[#0d1420] border border-slate-800 rounded-lg hover:border-sky-500/50 hover:-translate-y-1 transition-[transform,border-color] duration-200 overflow-hidden h-full"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-slate-900">
        {article.cover_image || article.doc_pages?.[0] ? (
          <BlurImage
            path={article.cover_image || article.doc_pages[0]}
            w={640}
            fill
            alt={article.title}
            className="object-cover object-top group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700">
            <FileText className="h-10 w-10" />
          </div>
        )}
      </div>
      <div className="p-6">
        <p className="text-xs text-slate-500">
          {fmtDate(article.created_at)}
        </p>
        <h3 className="mt-3 font-cond font-bold text-2xl uppercase tracking-tight text-slate-100 group-hover:text-sky-400 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-slate-400 line-clamp-3 leading-relaxed">
          {article.excerpt}
        </p>
      </div>
    </Link>
  </motion.div>
);

export const Articles = () => {
  const { content } = useContent();
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/articles")
      .then((res) => setArticles(res.data))
      .finally(() => setLoaded(true));
  }, []);

  const display = articles.filter((a) => a.featured);
  if (!loaded || display.length === 0) return null;

  return (
    <section id="writing" data-testid="articles-section" className="py-24 border-t border-slate-800/60">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-12">
          <h2 className="font-cond font-bold uppercase tracking-tight text-3xl lg:text-4xl text-slate-100">
            {content.articles?.heading || "Articles & Discoveries"}
          </h2>
          <Link
            to="/articles"
            data-testid="view-all-articles"
            className="group inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300 transition-colors whitespace-nowrap"
          >
            View all
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {display.map((a, i) => (
            <ArticleCard key={a.id} article={a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
